import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  Breadcrumbs, 
  Link, 
  Typography, 
  Box, 
  Toolbar, 
  Button, 
  useTheme,
  styled,
  useMediaQuery 
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

const StyledBreadcrumbsContainer = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: theme.mixins.toolbar.minHeight,
  zIndex: theme.zIndex.appBar - 1,
  background: `linear-gradient(to right, ${theme.palette.background.default}, ${theme.palette.background.paper})`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  backdropFilter: 'blur(8px)',
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: '48px !important',
  padding: theme.spacing(0, 2),
}));

const NavigationButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1.5),
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: theme.shadows[1],
  },
}));



const BreadCrumb = ({ links = [], currentPage = '' }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <StyledBreadcrumbsContainer>
      <StyledToolbar variant="dense" disableGutters>
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <NavigationButton
            variant="text"
            size="small"
            startIcon={<ArrowBack fontSize="small" />}
            onClick={() => navigate(-1)}
            color="inherit"
            aria-label="Go back"
          >
            {!isMobile && 'Back'}
          </NavigationButton>

          <Breadcrumbs 
            aria-label="breadcrumb"
            separator={
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ mx: 0.5 }}
              >
                ›
              </Typography>
            }
            sx={{ flexGrow: 1 }}
          >
            {links.map((link, index) => (
              <Link
                key={`${index}-${link.href}`}
                component={RouterLink}
                to={link.href}
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  transition: theme.transitions.create('color'),
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                  },
                }}
              >
                {link.label}
              </Link>
            ))}
            {currentPage && (
              <Typography 
                variant="subtitle2" 
                color="text.primary"
                fontWeight="500"
              >
                {currentPage}
              </Typography>
            )}
          </Breadcrumbs>
        </Box>
      </StyledToolbar>
    </StyledBreadcrumbsContainer>
  );
};
BreadCrumb.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  currentPage: PropTypes.string
};

export default BreadCrumb;
